import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLibrary } from '../context/LibraryContext';
import BookCard from '../components/BookCard';

const STATUS_LABELS = {
  all: 'All Books',
  want: 'Want to Read',
  reading: 'Currently Reading',
  finished: 'Finished',
};

const EMPTY_MESSAGES = {
  all: { emoji: '📚', title: 'Your library is empty', desc: "Start saving books you love and they'll appear here." },
  want: { emoji: '🌟', title: 'No books in your reading list yet', desc: 'Find books you want to read and add them to your list.' },
  reading: { emoji: '📖', title: "You're not reading anything yet", desc: 'Mark a book as "Currently Reading" to track your progress.' },
  finished: { emoji: '🏆', title: 'No finished books yet', desc: 'Complete a book and mark it as finished to see it here.' },
};

function StatCard({ icon, value, label, color }) {
  return (
    <div className="rounded-2xl p-5 flex flex-col gap-3 border border-border/60 glass-card"
      style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl"
        style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
        {icon}
      </div>
      <div>
        <div className="font-display text-3xl font-bold" style={{ color }}>{value}</div>
        <div className="text-gray-400 text-sm mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function Library() {
  const { bookmarks, readingStatus, getStats } = useLibrary();
  const [activeFilter, setActiveFilter] = useState('all');

  const stats = getStats();
  const allSavedBooks = Object.values(bookmarks);
  const filteredBooks = activeFilter === 'all'
    ? allSavedBooks
    : allSavedBooks.filter((book) => readingStatus[String(book.id)] === activeFilter);
  const empty = EMPTY_MESSAGES[activeFilter];

  const filterTabs = [
    { key: 'all', label: 'All', count: allSavedBooks.length },
    { key: 'want', label: 'Want to Read', count: allSavedBooks.filter((b) => readingStatus[String(b.id)] === 'want').length },
    { key: 'reading', label: 'Reading', count: allSavedBooks.filter((b) => readingStatus[String(b.id)] === 'reading').length },
    { key: 'finished', label: 'Finished', count: allSavedBooks.filter((b) => readingStatus[String(b.id)] === 'finished').length },
  ];

  return (
    <div className="min-h-screen py-10 animate-fade-in-up">
      <div className="page-container">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #e94560, #f5a623)' }}>
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </div>
            <h1 className="font-display text-3xl font-bold text-white">My Library</h1>
          </div>
          <p className="text-gray-400 text-sm ml-1">Your personal collection of saved books and reading progress</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon="🔖" value={stats.saved} label="Total Saved" color="#e94560" />
          <StatCard icon="📚" value={Object.values(readingStatus).filter((s) => s === 'want').length} label="Want to Read" color="#6366f1" />
          <StatCard icon="📖" value={stats.reading} label="Currently Reading" color="#f5a623" />
          <StatCard icon="✅" value={stats.finished} label="Finished" color="#22c55e" />
        </div>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {filterTabs.map((tab) => (
            <button key={tab.key} type="button" onClick={() => setActiveFilter(tab.key)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                transition-all duration-200 focus:outline-none ${
                activeFilter === tab.key
                  ? 'bg-accent text-white shadow-lg shadow-accent/30'
                  : 'text-gray-400 border border-border hover:text-white hover:border-accent/50 hover:bg-white/5'
              }`}>
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  activeFilter === tab.key ? 'bg-white/20 text-white' : 'bg-white/10 text-gray-400'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {filteredBooks.length > 0 ? (
          <div>
            <p className="text-gray-500 text-sm mb-5">
              Showing {filteredBooks.length} book{filteredBooks.length !== 1 ? 's' : ''} — {STATUS_LABELS[activeFilter]}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
              {filteredBooks.map((book) => (
                <BookCard key={`${book.source}-${book.id}`} book={book} />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="text-6xl mb-6">{empty.emoji}</div>
            <h2 className="font-display text-2xl font-bold text-white mb-3">{empty.title}</h2>
            <p className="text-gray-400 text-sm mb-8 max-w-sm">{empty.desc}</p>
            <Link to="/browse"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm
                         text-white transition-all duration-200 hover:shadow-lg hover:shadow-accent/30 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #e94560, #f5a623)' }}>
              Browse Books
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Library;
